import { Breadcrumbs } from '@liveauctioneers/hammer-ui-core/breadcrumbs';
import { CaretRight } from '@liveauctioneers/hammer-ui-core/icons';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ReactNode, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { useNewCatalogPageBannerExperiment } from '@/utils/amplitude/experiments/hooks/useNewCatalogPageBanner.experiment';
import { isCatalogPage } from '@/utils/urls';

export type Crumb = {
    back?: boolean;
    label: ReactNode;
    link?: string;
    onClick?: () => void;
};

type Props = {
    className?: string;
    crumbs: Crumb[];
    isPriceResultsRedesign?: boolean;
};

// This breadcrumb wrapper is to provide consistent styles and less code duplication for breadcrumbs that are used
// across the app, except for the item page.
export const BreadcrumbWrapper = ({ className, crumbs, isPriceResultsRedesign = false }: Props) => {
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = mergedCaterwaulAndHammerUIThemes;
    const narrow = useIsNarrowerThan(tabletNarrowWidthNum + 1);
    const navigate = useNavigate();
    const isNewCatalogPageBannerTreatment = useNewCatalogPageBannerExperiment();
    const { pathname } = useLocation();
    const catalogPage = isCatalogPage(pathname);
    const isInExperiment = isNewCatalogPageBannerTreatment && catalogPage;

    const onClick = useCallback(
        (href: string) => {
            navigate(href);
        },
        [navigate]
    );

    // HUI Breadcrumbs uses href as a React key, so crumbs without a link (current page) must be
    // rendered separately to avoid key-prop warnings.
    const linkedCrumbs = crumbs
        .filter((crumb) => Boolean(crumb.link))
        .map((crumb) => ({
            href: crumb.link!,
            label: String(crumb.label),
        }));

    const lastCrumb = crumbs.length > 0 ? crumbs[crumbs.length - 1] : null;
    const currentPageLabel = lastCrumb && !lastCrumb.link ? String(lastCrumb.label) : null;

    const breadcrumbsContent = (
        <div className="flex items-center">
            <Breadcrumbs
                breadcrumbs={linkedCrumbs}
                className={isPriceResultsRedesign ? 'mb-0' : undefined}
                onClick={onClick}
                showBackLink={false}
            />
            {currentPageLabel && linkedCrumbs.length > 0 && (
                <>
                    <CaretRight
                        className="mx-2xs max-h-[14px] min-h-[14px] max-w-[14px] min-w-[14px]"
                        color="primary"
                        role="none"
                        size="xs"
                    />
                    <span className="text-xs text-text-secondary">{currentPageLabel}</span>
                </>
            )}
            {currentPageLabel && linkedCrumbs.length === 0 && (
                <span className="text-xs text-text-secondary">{currentPageLabel}</span>
            )}
        </div>
    );

    if (!narrow) {
        return (
            <div
                className={cn(
                    isPriceResultsRedesign
                        ? 'my-0 w-full py-xs'
                        : cn(
                              'mx-auto w-[calc(100%-40px)] max-w-[1240px]',
                              isInExperiment ? 'my-sm' : 'my-lg',
                              className
                          )
                )}
            >
                {breadcrumbsContent}
            </div>
        );
    }

    if (isPriceResultsRedesign) {
        return (
            <div className="relative z-[1] w-full overflow-hidden px-sm py-xs">
                <div className="w-full min-w-0 overflow-x-auto overflow-y-hidden whitespace-nowrap">
                    {breadcrumbsContent}
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn(
                'relative z-[1] mx-md flex h-[18px] max-w-[1200px] items-center justify-between overflow-hidden',
                isInExperiment ? 'my-sm' : 'my-lg',
                className
            )}
        >
            <div
                className="m-0 inline-block w-full overflow-x-auto overflow-y-hidden whitespace-nowrap"
                style={{ overflow: '-moz-scrollbars-none' }}
            >
                {breadcrumbsContent}
            </div>
        </div>
    );
};
